import React from 'react';
import Select, { mergeStyles } from 'react-select';
import AsyncSelect from 'react-select/async';

const appSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        border: state.isFocused ? '1px solid rgba(130, 22, 146, 0.8)' : '1px solid #ccc',
        borderRadius: '5px',
        minHeight: '45px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(130, 22, 146, 0.12)' : 'none',
        '&:hover': {
            borderColor: 'rgba(130, 22, 146, 0.8)',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: '1rem',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 20,
    }),
    // When the menu is portalled (menuPortalTarget={document.body}) it must sit above
    // modals (StyledModal z-index: 1050), otherwise the dropdown renders behind the modal
    // and looks empty.
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1100,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? 'rgba(130, 22, 146, 0.1)' : 'white',
        color: '#111827',
        '&:hover': {
            backgroundColor: 'rgba(130, 22, 146, 0.2)',
        },
    }),
};

const normalize = (value) => (value === undefined || value === null ? '' : String(value));
const getOptionValue = (value) => {
    if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
        return value.value;
    }

    return value;
};

const BaseSelect = ({
    component: SelectComponent = Select,
    options = [],
    value = '',
    onChange,
    placeholder,
    inputId,
    isClearable = false,
    isSearchable = true,
    isMulti = false,
    styles,
    children,
    ...rest
}) => {
    const optionsFromChildren =
        options.length > 0
            ? options
            : React.Children.toArray(children)
                .filter((child) => React.isValidElement(child) && child.type === 'option')
                .map((child) => ({
                    value: child.props.value,
                    label: child.props.children,
                }));

    const selectedOption = isMulti
        ? optionsFromChildren.filter((option) =>
            (Array.isArray(value) ? value : [])
                .map((entry) => normalize(getOptionValue(entry)))
                .includes(normalize(option?.value))
        )
        : optionsFromChildren.find((option) => normalize(option?.value) === normalize(getOptionValue(value)))
            || (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value') ? value : null);

    const resolvedStyles = styles ? mergeStyles(appSelectStyles, styles) : appSelectStyles;

    return (
        <SelectComponent
            inputId={inputId}
            options={optionsFromChildren}
            value={selectedOption}
            onChange={(selected) => {
                const nextValue = isMulti
                    ? (selected || []).map((option) => option?.value)
                    : (selected?.value ?? '');
                const syntheticEvent = {
                    target: {
                        name: inputId,
                        value: nextValue,
                    },
                };

                // Legacy compatibility: first arg behaves like native event,
                // second arg contains the plain selected value.
                onChange?.(syntheticEvent, nextValue, selected);

                if (isMulti) {
                    return;
                }
            }}
            placeholder={placeholder}
            isClearable={isClearable}
            isSearchable={isSearchable}
            isMulti={isMulti}
            styles={resolvedStyles}
            classNamePrefix="app-select"
            {...rest}
        />
    );
};

export const SearchableSelect = (props) => (
    <BaseSelect
        isSearchable
        isClearable={props.isClearable ?? true}
        {...props}
    />
);

export const SimpleSelect = (props) => (
    <BaseSelect
        isSearchable={false}
        isClearable={false}
        {...props}
    />
);

export const AsyncSearchableSelect = (props) => (
    <BaseSelect
        component={AsyncSelect}
        isSearchable
        isClearable={props.isClearable ?? true}
        {...props}
    />
);

export default SearchableSelect;
